// src/features/locations/CandidatesPanel.jsx
//
// Slice 5b: Prospecting candidates panel. Renders only when the parent
// location's stage === "prospecting" (gating is the parent's job — this
// component renders nothing when its `location` prop has any other stage,
// to keep the gate co-located with the data and avoid a stray empty
// header on operational sites).
//
// Visual / behavioral parity with the legacy ProspectingCandidatesPanel:
//   - CAND_STATUS palette mirrors the legacy under_review / shortlisted /
//     active / paused / rejected color & background (T tokens).
//   - Form fields mirror EMPTY_CAND: name, address, floor, visitedDate,
//     status, leaseType, broker, pros[], cons[].
//   - pros/cons render as a list of inline text inputs with +/- controls.
//
// Field-type map for the diff helper:
//   { status: "enum", visitedDate: "date", pros: "array", cons: "array" }
// "array" was added to src/utils/diff.js in this slice for pros/cons.
//
// Audit emission lives in src/data/resourcesClient.js; this panel's
// responsibility ends at calling the right mutator with a `before`
// snapshot. Sort: server returns rows by created_at DESC; we re-sort
// locally on the same key for stability against any in-place mutation.

import React from "react";
import {
  listCandidates,
  addCandidate,
  updateCandidate,
  deleteCandidate,
} from "../../data/resourcesClient.js";
import { T } from "../../app/tokens.js";
import { fmtDate } from "../../utils/format.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { diffPatch } from "../../utils/diff.js";
import { log } from "../../utils/logger.js";

// ─── Constants ────────────────────────────────────────────────────────────

// Ported verbatim from the legacy CAND_STATUS map (line 574 of the
// legacy HTML). Keep the keys and the T-token values identical so the
// CAND_STATUS-shape tests in the legacy suite stay green if they're
// ever re-pointed at this module.
const CAND_STATUS = {
  under_review: { label: "Under Review", color: T.blue,   bg: T.blueXLight ?? T.blueSoft },
  shortlisted:  { label: "Shortlisted",  color: T.amber,  bg: T.utilAmber  },
  active:       { label: "Active",       color: T.green,  bg: T.utilGreen  },
  paused:       { label: "Paused",       color: T.mid,    bg: T.lightGray  },
  rejected:     { label: "Rejected",     color: T.red,    bg: T.redSoft ?? T.utilRed },
};
const STATUS_OPTIONS = Object.entries(CAND_STATUS).map(([value, meta]) => ({
  value, label: meta.label,
}));

const FIELD_TYPES = {
  status:      "enum",
  visitedDate: "date",
  pros:        "array",
  cons:        "array",
};

// Inline button styles — match the CriticalDates / WatchItems shapes.
const PRIMARY_BUTTON   = { background: T.blue, color: T.white, border: "none", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 18px", cursor: "pointer" };
const SECONDARY_BUTTON = { background: T.white, color: T.mid, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold, padding: "6px 14px", cursor: "pointer" };

// ─── Pure helpers ─────────────────────────────────────────────────────────

function emptyForm() {
  // Mirrors EMPTY_CAND from the legacy file (line 581) — pros/cons start
  // with one blank entry so the user has a row to type into.
  return {
    name: "", address: "", floor: "", visitedDate: "",
    status: "under_review", leaseType: "", broker: "",
    pros: [""], cons: [""],
  };
}

function trimList(list) {
  // Drop blank entries and trim each remaining one. The server stores
  // pros/cons as TEXT[]; sending [""] would persist a literal empty
  // string which is almost never what the user meant.
  if (!Array.isArray(list)) return [];
  return list.map((s) => String(s ?? "").trim()).filter((s) => s.length > 0);
}

function normalize(form) {
  return {
    name:        (form.name        || "").trim(),
    address:     (form.address     || "").trim() || null,
    floor:       (form.floor       || "").trim() || null,
    visitedDate: form.visitedDate  || null,
    status:      form.status,
    leaseType:   (form.leaseType   || "").trim() || null,
    broker:      (form.broker      || "").trim() || null,
    pros:        trimList(form.pros),
    cons:        trimList(form.cons),
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────

function StringListEditor({ label, items = [], onChange, busy, placeholder }) {
  // Inline editor for a TEXT[] field. Buttons read "+ Add row" / "×".
  const labelStyle = { fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };
  const inputStyle = { flex: 1, padding: "6px 10px", fontSize: T.fsSm, border: `1px solid ${T.border}`, borderRadius: 6, color: T.dark, background: T.white, outline: "none" };
  const list = items.length > 0 ? items : [""];
  const setAt = (i, v) => onChange(list.map((x, j) => (j === i ? v : x)));
  const removeAt = (i) => onChange(list.length === 1 ? [""] : list.filter((_x, j) => j !== i));
  const addRow = () => onChange([...list, ""]);
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {list.map((value, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              value={value}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder={placeholder}
              disabled={busy}
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              disabled={busy}
              aria-label={`Remove ${label} row ${i + 1}`}
              style={{ ...SECONDARY_BUTTON, padding: "4px 8px", fontSize: T.fsBase }}
            >×</button>
          </div>
        ))}
        <div>
          <button type="button" onClick={addRow} disabled={busy} style={SECONDARY_BUTTON}>
            + Add {label.toLowerCase()}
          </button>
        </div>
      </div>
    </div>
  );
}

function CandidateForm({ initial, mode, onSave, onCancel, busy, error }) {
  const [form, setForm] = React.useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const labelStyle = { fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "6px 10px", fontSize: T.fsSm, border: `1px solid ${T.border}`, borderRadius: 6, color: T.dark, background: T.white, outline: "none", boxSizing: "border-box" };
  const ok = (form.name || "").trim().length > 0 && !!form.status;

  return (
    <div style={{ background: T.card, border: `1px solid ${T.blue}`, borderRadius: 8, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, marginBottom: 12 }}>
        {mode === "edit" ? "Edit candidate" : "Add candidate"}
      </div>
      {error && <ErrorBanner error={error} />}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <div style={labelStyle}>Name</div>
          <input value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder="Building or address" style={inputStyle} disabled={busy} />
        </div>
        <div>
          <div style={labelStyle}>Status</div>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} style={inputStyle} disabled={busy}>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={labelStyle}>Address</div>
        <input value={form.address || ""} onChange={(e) => set("address", e.target.value)} style={inputStyle} disabled={busy} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <div style={labelStyle}>Floor</div>
          <input value={form.floor || ""} onChange={(e) => set("floor", e.target.value)} style={inputStyle} disabled={busy} />
        </div>
        <div>
          <div style={labelStyle}>Visited date</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <DateInput value={form.visitedDate || ""} onChange={(e) => set("visitedDate", e.target.value)} style={{ ...inputStyle, flex: 1 }} disabled={busy} />
            {form.visitedDate && (
              <button
                type="button"
                data-testid="candidate-form-clear-visitedDate"
                onClick={() => set("visitedDate", "")}
                title="Clear date"
                aria-label="Clear date"
                disabled={busy}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.mid,
                  fontSize: T.fsMd,
                  lineHeight: 1,
                  padding: "4px 2px",
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={labelStyle}>Lease type</div>
          <input value={form.leaseType || ""} onChange={(e) => set("leaseType", e.target.value)}
            placeholder="Direct Lease, Sublease, …" style={inputStyle} disabled={busy} />
        </div>
        <div>
          <div style={labelStyle}>Broker</div>
          <input value={form.broker || ""} onChange={(e) => set("broker", e.target.value)} style={inputStyle} disabled={busy} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
        <StringListEditor label="Pros" items={form.pros || [""]}
          onChange={(next) => set("pros", next)} busy={busy}
          placeholder="e.g. Subway access" />
        <StringListEditor label="Cons" items={form.cons || [""]}
          onChange={(next) => set("cons", next)} busy={busy}
          placeholder="e.g. Old elevators" />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="wos-btn" onClick={() => onSave(form)} disabled={!ok || busy}
          style={{ ...PRIMARY_BUTTON, opacity: (!ok || busy) ? 0.5 : 1, cursor: (!ok || busy) ? "default" : "pointer" }}>
          {busy ? "Saving…" : (mode === "edit" ? "Save changes" : "Add candidate")}
        </button>
        <button className="wos-btn" onClick={onCancel} disabled={busy} style={SECONDARY_BUTTON}>Cancel</button>
      </div>
    </div>
  );
}

// Type-the-name delete confirm. Candidates use soft delete on the server;
// deleted candidates appear in Admin → Recently Deleted and can be restored
// within 30 days. The type-to-confirm guard prevents accidental deletes.
function DeleteConfirm({ row, onConfirm, onCancel, busy, error }) {
  const target = (row.name || "").trim();
  const [typed, setTyped] = React.useState("");
  const ok = typed.trim() === target && target.length > 0;
  return (
    <div style={{ background: T.card, border: `1px solid ${T.red}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.red, marginBottom: 4 }}>Remove this candidate?</div>
      <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 8, lineHeight: 1.5 }}>
        This moves the candidate to Recently deleted. You can restore it for 30 days from Admin → Recently deleted.
      </div>
      <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 8, lineHeight: 1.5 }}>
        To confirm, type the candidate name exactly:
        {" "}<code style={{ background: T.lightGray, padding: "1px 6px", borderRadius: 3 }}>{target}</code>
      </div>
      {error && <ErrorBanner error={error} />}
      <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type name to confirm"
        style={{ width: "100%", padding: "6px 10px", fontSize: T.fsSm, border: `1px solid ${T.border}`, borderRadius: 6, marginBottom: 8, boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button className="wos-btn" onClick={onConfirm} disabled={!ok || busy}
          style={{ background: T.red, color: T.white, border: "none", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwBold, padding: "5px 14px", cursor: (ok && !busy) ? "pointer" : "default", opacity: (ok && !busy) ? 1 : 0.5 }}>
          {busy ? "Removing…" : "Remove candidate"}
        </button>
        <button className="wos-btn" onClick={onCancel} disabled={busy} style={SECONDARY_BUTTON}>Cancel</button>
      </div>
    </div>
  );
}

function ProsConsList({ heading, items, color }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        {heading}
      </div>
      <ul style={{ margin: 0, paddingLeft: 16, fontSize: T.fsSm, color: T.dark }}>
        {items.map((s, i) => (
          <li key={i} style={{ color }}>{s}</li>
        ))}
      </ul>
    </div>
  );
}

function CandidateRow({ cand, canWrite, onEdit, onDelete }) {
  const st = CAND_STATUS[cand.status] || CAND_STATUS.under_review;
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>{cand.name}</span>
            <span style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: st.color, background: st.bg, padding: "1px 6px", borderRadius: 4 }}>
              {st.label}
            </span>
          </div>
          <div style={{ fontSize: T.fsSm, color: T.mid, lineHeight: 1.55 }}>
            {[
              cand.address,
              cand.floor && `Floor ${cand.floor}`,
              cand.visitedDate && `Visited ${fmtDate(cand.visitedDate)}`,
              cand.leaseType,
              cand.broker && `Broker: ${cand.broker}`,
            ].filter(Boolean).join(" · ") || <span style={{ color: T.light }}>No details</span>}
          </div>
          {((cand.pros && cand.pros.length > 0) || (cand.cons && cand.cons.length > 0)) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
              <ProsConsList heading="Pros" items={cand.pros} color={T.green} />
              <ProsConsList heading="Cons" items={cand.cons} color={T.red} />
            </div>
          )}
        </div>
        {canWrite && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={() => onEdit(cand)}
              style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid, background: T.hoverBg, border: `1px solid ${T.border}`, borderRadius: 5, padding: "3px 10px", cursor: "pointer" }}>
              Edit
            </button>
            <button onClick={() => onDelete(cand)}
              style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.red, background: T.utilRed, border: "none", borderRadius: 5, padding: "3px 10px", cursor: "pointer" }}>
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {object}   props.location   Parent location row (id + stage used).
 * @param {boolean}  props.canWrite   Editor or admin can Add / Edit / Delete.
 * @param {Function} [props.onChanged] Optional refetch hook for the parent.
 *
 * Returns null when stage !== "prospecting" (gate is here, not at the
 * call-site, so the LocationDetail render reads as a flat sequence of
 * panels without conditional clutter).
 */
export function CandidatesPanel({ location, canWrite, onChanged }) {
  if (!location || location.stage !== "prospecting") return null;
  const locationId = location.id;

  const [rows, setRows]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadErr, setLoadErr] = React.useState(null);

  const [mode, setMode]             = React.useState("view"); // 'view' | 'add' | 'edit'
  const [editing, setEditing]       = React.useState(null);
  const [confirmDel, setConfirmDel] = React.useState(null);
  const [busy, setBusy]             = React.useState(false);
  const [formError, setFormError]   = React.useState(null);

  const reload = React.useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      const data = await listCandidates(locationId);
      // Stable client-side sort matching server order (created_at DESC, id DESC).
      data.sort((a, b) => {
        const ac = String(a.createdAt || "");
        const bc = String(b.createdAt || "");
        if (ac !== bc) return ac < bc ? 1 : -1;
        return String(a.id) < String(b.id) ? 1 : -1;
      });
      setRows(data);
    } catch (err) {
      log.warn("CandidatesPanel: list failed", { err: String(err) });
      setLoadErr(err);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  React.useEffect(() => { reload(); }, [reload]);

  function startAdd()       { setFormError(null); setEditing(null); setMode("add"); }
  function startEdit(row)   { setFormError(null); setEditing(row);  setMode("edit"); }
  function cancel()         { setFormError(null); setEditing(null); setMode("view"); }
  function startDelete(row) { setFormError(null); setConfirmDel(row); }
  function cancelDelete()   { setFormError(null); setConfirmDel(null); }

  async function onSubmitAdd(form) {
    setBusy(true); setFormError(null);
    try {
      await addCandidate(locationId, normalize(form));
      setMode("view");
      await reload();
      onChanged?.();
    } catch (err) {
      log.warn("CandidatesPanel: add failed", { err: String(err) });
      setFormError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitEdit(form) {
    setBusy(true); setFormError(null);
    try {
      const next = normalize(form);
      const patch = diffPatch(next, editing, FIELD_TYPES);
      if (Object.keys(patch).length === 0) {
        setMode("view"); setEditing(null);
        return;
      }
      await updateCandidate(locationId, editing.id, patch, { before: editing });
      setMode("view"); setEditing(null);
      await reload();
      onChanged?.();
    } catch (err) {
      log.warn("CandidatesPanel: update failed", { err: String(err) });
      setFormError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmDelete() {
    if (!confirmDel) return;
    setBusy(true); setFormError(null);
    const target = confirmDel;
    try {
      await deleteCandidate(locationId, target.id, { before: target });
      setConfirmDel(null);
      await reload();
      onChanged?.();
    } catch (err) {
      log.warn("CandidatesPanel: delete failed", { err: String(err) });
      setFormError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <h4 style={{ margin: 0, flex: 1, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
          Prospecting Candidates {!loading && <span style={{ fontWeight: T.fwRegular, color: T.light }}>({rows.length})</span>}
        </h4>
        {canWrite && mode === "view" && !confirmDel && (
          <button onClick={startAdd} style={PRIMARY_BUTTON}>+ Add candidate</button>
        )}
      </div>

      {loadErr && <ErrorBanner error={loadErr} onDismiss={() => setLoadErr(null)} />}

      {mode === "add" && (
        <CandidateForm initial={emptyForm()} mode="add"
          onSave={onSubmitAdd} onCancel={cancel} busy={busy} error={formError} />
      )}
      {mode === "edit" && editing && (
        <CandidateForm
          initial={{
            name:        editing.name        || "",
            address:     editing.address     || "",
            floor:       editing.floor       || "",
            visitedDate: String(editing.visitedDate || "").slice(0, 10),
            status:      editing.status      || "under_review",
            leaseType:   editing.leaseType   || "",
            broker:      editing.broker      || "",
            pros:        editing.pros && editing.pros.length > 0 ? [...editing.pros] : [""],
            cons:        editing.cons && editing.cons.length > 0 ? [...editing.cons] : [""],
          }}
          mode="edit"
          onSave={onSubmitEdit} onCancel={cancel} busy={busy} error={formError}
        />
      )}
      {confirmDel && (
        <DeleteConfirm row={confirmDel} onConfirm={onConfirmDelete} onCancel={cancelDelete} busy={busy} error={formError} />
      )}

      {!loading && rows.length === 0 && mode === "view" && !confirmDel && (
        <div style={{ fontSize: T.fsSm, color: T.light, padding: "8px 2px" }}>
          No candidates on this location yet.
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((c) => (
            <CandidateRow key={c.id} cand={c}
              canWrite={canWrite}
              onEdit={startEdit} onDelete={startDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

// Task-579 — exported for isolated empty-prop guard tests only.
// Do not use StringListEditor in application code outside this module.
export const __testables = { StringListEditor };
